// 2026-05-11 Phase 4-A: 口座引落請求書ページ
// ----------------------------------------------------------------------------
// 「口座引落」(= bank_auto_debit) フラグが立った請求書の専用処理 page。
// 25日払い (= JournalVerificationPage) とは仕訳ルールが異なるため (= 期日 = 引落日、
// 自動登録ルール経由で消込、 accrual 禁止) UI を分離。
//
// データソース: `GET /api/deal-proposals?flow_type=bank_auto_debit`
//   - flow_type filter は worker 側で audit_summary.bank_auto_debit_detected = 1
//     OR proposed_deal_json.flow_type = 'bank_auto_debit' で抽出。
//
// レイアウト: JournalVerificationPage と同等 (KPI + entries table + 右側 side panel)。
//   - 月次 selector / 一括登録 button は本 page では非表示 (= 別途運用)。
//   - KPI: 件数 / 合計金額 / 直近の引落日 group。
// ----------------------------------------------------------------------------

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  PageTitle,
  SectionTitle,
  SubSectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  VStack,
  Button,
  SearchField,
  SelectBox,
  CheckBox,
  Text,
  Note,
  StatusIcon,
  Breadcrumbs,
  MessageBlock,
  FloatingMessageBlock,
  TabBar,
} from '@freee_jp/vibes';
import { MdAccountBalanceWallet } from 'react-icons/md';
import { JournalEntry, VerificationStatus, JournalDetail } from '../types/journal';
import { autoDebitProposalToUnified } from '../lib/statusVocab';
import { PdfViewer } from '../components/PdfViewer';
import { CorrectionModal, CorrectionAction } from '../components/CorrectionModal';
import {
  fetchDealProposalsByFlowType,
  approveDealProposal,
  rejectDealProposal,
  DealProposal,
} from '../api/client';
import type { PdfViewerFile } from '../components/PdfViewer';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function receiptsForProposal(p: DealProposal | null | undefined): PdfViewerFile[] {
  if (!p) return [];
  const deal: any = p.proposed_deal_json || {};
  const attached: any[] = Array.isArray(deal.attached_rowids) ? deal.attached_rowids : [];
  if (attached.length > 0) {
    return attached.map((item: any, i: number) => {
      const label = (typeof item === 'object' && item.label)
        ? item.label
        : (i === 0 ? '主証憑' : `添付 ${i}`);
      if (typeof item === 'object' && item.url) return { url: item.url, label };
      const rowid = typeof item === 'object' ? item.rowid : item;
      return {
        url: `https://invoice-review.system-0f7.workers.dev/pdf/${rowid}`,
        label,
      };
    });
  }
  if (p.pdf_rowid) {
    const ridInt = parseInt(String(p.pdf_rowid), 10);
    if (Number.isFinite(ridInt) && ridInt > 0) {
      return [{
        url: `https://invoice-review.system-0f7.workers.dev/pdf/${ridInt}`,
        label: '主証憑',
      }];
    }
  }
  return [];
}

function proposalToEntry(p: DealProposal): JournalEntry {
  const deal: any = p.proposed_deal_json || {};
  const audit: any = p.audit_summary || {};
  const details: any[] = Array.isArray(deal.details) ? deal.details : [];
  const firstSection = details[0]?.sectionName || details[0]?.section_name || '—';
  const status: VerificationStatus =
    p.status === 'registered' ? 'approved'
    : p.status === 'approved' ? 'approved'
    : p.status === 'rejected' ? 'blocked'
    : 'needs_review';

  // Phase 4-A: 引落関連 flags を visible に注入
  const flags: string[] = [];
  if (p.status === 'proposed') flags.push('要承認');
  else if (p.status === 'rejected') flags.push('却下済');
  if (audit?.bank_auto_debit_detected) flags.push('口座引落');
  if (audit?.payment_pattern) flags.push(`pattern: ${audit.payment_pattern}`);

  return {
    id: `proposal_${p.id}`,
    rowid: parseInt(String(p.pdf_rowid || '0'), 10) || 0,
    refNumber: p.ref_number,
    partnerName: p.partner_name || `partner_id: ${p.partner_id}`,
    partnerId: p.partner_id,
    documentType: 'invoice_autopay',
    verifiedType: 'invoice_autopay',
    issueDate: p.issue_date || '',
    dueDate: p.due_date || audit?.bank_auto_debit_date || '',
    grandTotal: p.amount || details.reduce((s: number, d: any) => s + (d.amount || 0), 0),
    taxBreakdown: [],
    details: details.map((d: any, idx: number) => ({
      index: idx,
      accountName: d.accountName || d.account_item_name || d.account_name || (d.account_item_id || d.accountItemId ? `#${d.account_item_id || d.accountItemId}` : '—'),
      accountItemId: d.account_item_id || d.accountItemId || 0,
      taxCode: d.tax_code || d.taxCode || 0,
      taxCodeName: d.taxCodeName || d.tax_code_name || '',
      sectionName: d.sectionName || d.section_name || '',
      sectionId: d.section_id || d.sectionId || 0,
      itemName: d.itemName || d.item_name || null,
      amount: d.amount || 0,
      taxAmount: d.taxAmount || d.tax_amount || 0,
      description: d.description || '',
      mappingSource: null,
    })),
    storeName: (() => {
      const uniq = Array.from(new Set(
        details.map((d: any) => d.sectionName || d.section_name).filter(Boolean),
      ));
      if (uniq.length === 1) return uniq[0];
      if (uniq.length > 1) return `${uniq.length}店舗按分`;
      return firstSection;
    })(),
    status,
    alreadyRegistered: p.status === 'registered',
    registeredDealId: p.freee_deal_id ?? null,
    confidence: 1.0,
    flags,
    notes: p.rejection_reason || p.notes || (audit?.bank_auto_debit_account ? `引落口座: ${audit.bank_auto_debit_account}` : ''),
    pdfUrl: (() => {
      const n = p.pdf_rowid ? parseInt(String(p.pdf_rowid), 10) : NaN;
      return Number.isFinite(n) && n > 0 ? `https://invoice-review.system-0f7.workers.dev/pdf/${n}` : '';
    })(),
    tNumber: null,
    invoiceNumber: null,
    patternStatus: 'Reviewed',
    a2aAgreement: true,
    dialogueRounds: 0,
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// 状態語彙は lib/statusVocab.ts (正本) に集約 — pending=承認判断待ち(通常) / needs_review=不備あり(修正)
const STATUS_OPTIONS = [
  { name: 'すべて', value: 'all' },
  { name: autoDebitProposalToUnified('pending').label, value: 'pending' },
  { name: autoDebitProposalToUnified('approved').label, value: 'approved' },
  { name: autoDebitProposalToUnified('needs_review').label, value: 'needs_review' },
  { name: autoDebitProposalToUnified('blocked').label, value: 'blocked' },
];

const statusToIcon = (status: VerificationStatus): 'success' | 'warning' | 'error' | 'progress' | 'required' | 'disabled' => {
  switch (status) {
    case 'approved': return 'success';
    case 'needs_review': return 'warning';
    case 'blocked': return 'disabled'; // 除外(却下) = グレー中立終端 (赤=失敗事実には使わない)
    default: return 'required';
  }
};

const statusToLabel = (status: VerificationStatus): string => autoDebitProposalToUnified(status).label;

const fmt = (n: number) => '¥' + n.toLocaleString('ja-JP');

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const S = {
  page: {
    background: '#f7f5f5',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,

  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,

  kpiCard: {
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  kpiAccent: (color: string) => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '3px',
    height: '100%',
    background: color,
    borderRadius: '3px 0 0 3px',
  }),

  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0',
    transition: 'grid-template-columns 0.25s ease',
  } as React.CSSProperties,

  mainLayoutWithPanel: {
    display: 'grid',
    gridTemplateColumns: '62% 38%',
    gap: '16px',
    alignItems: 'stretch',
  } as React.CSSProperties,

  thCell: {
    padding: '10px 12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid #dbd9d7',
    background: '#fafaf8',
  } as React.CSSProperties,

  tdCell: {
    padding: '12px 12px',
    fontSize: '0.8125rem',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #edebea',
  } as React.CSSProperties,

  row: (isSelected: boolean, isActive: boolean) => ({
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    background: isActive
      ? '#ebf3ff'
      : isSelected
        ? '#f0f8ff'
        : 'transparent',
    borderLeft: '3px solid transparent',
  }),

  badge: (bg: string, color: string) => ({
    display: 'inline-block',
    fontSize: '0.625rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    lineHeight: '1.4',
    background: bg,
    color: color,
    whiteSpace: 'nowrap' as const,
  }),

  flagBadge: {
    display: 'inline-block',
    fontSize: '0.5625rem',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: '10px',
    background: '#e8eaf6',
    color: '#3949ab',
    marginRight: '3px',
    marginTop: '2px',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  expandSection: {
    padding: '16px 16px 16px 48px',
    background: '#fafaf8',
    borderBottom: '2px solid #edebea',
  } as React.CSSProperties,

  detailTh: {
    padding: '6px 10px',
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    borderBottom: '1px solid #dbd9d7',
    background: '#f7f5f5',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  detailTd: {
    padding: '8px 10px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #edebea',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,

  sidePanel: {
    position: 'sticky' as const,
    top: '12px',
    maxHeight: 'calc(100vh - 24px)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    background: '#fff',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e5e4e2',
  } as React.CSSProperties,

  sidePanelHeader: {
    padding: '16px 20px 12px',
    borderBottom: '1px solid #edebea',
    background: '#fff',
  } as React.CSSProperties,

  sidePanelBody: {
    flex: 1,
    overflowY: 'auto' as const,
    background: '#fff',
  } as React.CSSProperties,

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '6px 0',
    borderBottom: '1px solid #f5f3f2',
    fontSize: '0.75rem',
  } as React.CSSProperties,

  metaLabel: {
    color: '#8a8580',
    fontSize: '0.6875rem',
    flexShrink: 0,
    marginRight: '12px',
  } as React.CSSProperties,

  metaValue: {
    color: '#252525',
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

export const BankAutoDebitInvoicePage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<'dueDate' | 'issueDate' | 'amount' | 'partnerName'>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [sidePanelTab, setSidePanelTab] = useState(0);
  const [proposalByEntryId, setProposalByEntryId] = useState<Record<string, DealProposal>>({});
  const [correctionEntryId, setCorrectionEntryId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // 2026-07-11 監査 fix (①/⑧型): status 別 fetch の失敗を console にも出さず握り潰すと、
  // 該当 status の行が黙って一覧から消える (= 部分的 UI 不可視)。失敗時は entries を
  // 更新せず (stale 表示維持)、画面に警告 banner を出す。
  const [loadError, setLoadError] = useState<string | null>(null);

  // Phase 4-A: bank_auto_debit のみ全 status を取得して merge
  useEffect(() => {
    let cancelled = false;

    const reload = async () => {
      if (correctionEntryId) return;
      try {
        const statuses = ['proposed', 'approved', 'rejected', 'registered'] as const;
        const all: DealProposal[] = [];
        const failedStatuses: string[] = [];
        for (const s of statuses) {
          try {
            const r = await fetchDealProposalsByFlowType('bank_auto_debit', s);
            all.push(...r.items);
          } catch (e) {
            console.error(`[FREEEMAN] bank_auto_debit fetch failed (status=${s}):`, e);
            failedStatuses.push(s);
          }
        }
        if (cancelled) return;
        if (failedStatuses.length > 0) {
          // 部分失敗: entries を差し替えると失敗 status の行が黙って消えるため、
          // 前回表示を維持して警告のみ。次の polling (8秒後) で自然回復を試みる。
          setLoadError(`一部データの取得に失敗しました (status: ${failedStatuses.join(', ')})。表示は最終取得時点のままです。`);
          setLoading(false);
          return;
        }
        setLoadError(null);
        const proposalEntries = all.map(proposalToEntry);
        const lookup: Record<string, DealProposal> = {};
        all.forEach((p) => { lookup[`proposal_${p.id}`] = p; });
        setProposalByEntryId(lookup);

        const rank = (s: string): number => ({ blocked: -1, pending: 0, needs_review: 1, approved: 2 }[s] ?? 0);

        setEntries((prev) => {
          const prevById = new Map(prev.map((e) => [e.id, e]));
          return proposalEntries.map((fresh) => {
            const local = prevById.get(fresh.id);
            if (!local) return fresh;
            if (fresh.alreadyRegistered) return fresh;
            if (rank(local.status) > rank(fresh.status)) {
              return { ...fresh, status: local.status };
            }
            return fresh;
          });
        });
        setLoading(false);
      } catch (e) {
        console.error('[FREEEMAN] bank_auto_debit proposals fetch failed:', e);
        if (!cancelled) {
          setLoadError(`データ取得に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
        }
        setLoading(false);
      }
    };

    reload();
    const t = setInterval(reload, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [correctionEntryId]);

  // Derived
  const filtered = useMemo(() => {
    const list = entries.filter((e) => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const searchable = [e.partnerName, e.storeName, e.invoiceNumber, e.tNumber]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
    const sorted = [...list].sort((a, b) => {
      let va: string | number = '', vb: string | number = '';
      switch (sortKey) {
        case 'dueDate':     va = a.dueDate || '';   vb = b.dueDate || '';   break;
        case 'issueDate':   va = a.issueDate || ''; vb = b.issueDate || ''; break;
        case 'amount':      va = a.grandTotal;      vb = b.grandTotal;      break;
        case 'partnerName': va = a.partnerName || ''; vb = b.partnerName || ''; break;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [entries, statusFilter, searchText, sortKey, sortDir]);

  const stats = useMemo(() => {
    const total = entries.length;
    const approved = entries.filter((e) => e.status === 'approved').length;
    const pending = entries.filter((e) => e.status === 'needs_review' || e.status === 'pending').length;
    const blocked = entries.filter((e) => e.status === 'blocked').length;
    const alreadyRegistered = entries.filter((e) => e.alreadyRegistered).length;
    const totalAmount = entries.reduce((s, e) => s + e.grandTotal, 0);
    // 引落日 group (= due_date by YYYY-MM-DD)
    const dueGroups: Record<string, number> = {};
    entries.forEach((e) => {
      if (!e.dueDate) return;
      dueGroups[e.dueDate] = (dueGroups[e.dueDate] || 0) + 1;
    });
    const dueGroupCount = Object.keys(dueGroups).length;
    const dueGroupList = Object.entries(dueGroups).sort();
    return { total, approved, pending, blocked, alreadyRegistered, totalAmount, dueGroupCount, dueGroupList };
  }, [entries]);

  const activeEntry = useMemo(
    () => (activePanelId ? entries.find((e) => e.id === activePanelId) ?? null : null),
    [entries, activePanelId]
  );

  // Handlers
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((e) => e.id))
    );
  }, [filtered]);

  const refByEntryId = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of entries) {
      const ref = e.refNumber || (e.id.startsWith('proposal_') ? `arica:${e.rowid}` : null);
      if (ref) m.set(e.id, ref);
    }
    return m;
  }, [entries]);

  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 6000);
  }, []);

  const updateStatus = useCallback((id: string, status: VerificationStatus) => {
    let previousStatus: VerificationStatus | null = null;
    setEntries((prev) => {
      const current = prev.find((e) => e.id === id);
      if (current) previousStatus = current.status;
      return prev.map((e) => (e.id === id ? { ...e, status } : e));
    });

    if (status === 'approved') {
      const ref = refByEntryId.get(id);
      if (!ref) return;
      approveDealProposal(ref).catch((err) => {
        console.error('[approve] relay failed:', err);
        pushToast({ kind: 'error', text: `承認失敗: ${err.message || err}` });
        if (previousStatus !== null) {
          const prevCopy = previousStatus;
          setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status: prevCopy } : e)));
        }
      });
      return;
    }

    if (status === 'blocked') {
      const ref = refByEntryId.get(id);
      if (!ref) return;
      rejectDealProposal(ref).catch((err) => {
        console.error('[reject] relay failed:', err);
        pushToast({ kind: 'error', text: `却下失敗: ${err.message || err}` });
        if (previousStatus !== null) {
          const prevCopy = previousStatus;
          setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status: prevCopy } : e)));
        }
      });
    }
  }, [refByEntryId, pushToast]);

  const approveSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const pairs = ids
      .map((id) => ({ id, ref: refByEntryId.get(id) }))
      .filter((p) => !!p.ref) as Array<{ id: string; ref: string }>;
    if (pairs.length === 0) return;

    const previousById = new Map<string, VerificationStatus>();
    setEntries((prev) => {
      prev.forEach((e) => { if (selectedIds.has(e.id)) previousById.set(e.id, e.status); });
      return prev.map((e) => (selectedIds.has(e.id) ? { ...e, status: 'approved' as const } : e));
    });
    setSelectedIds(new Set());

    const CONCURRENCY = 5;
    let cursor = 0;
    const failed: string[] = [];
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, pairs.length) }).map(async () => {
        while (cursor < pairs.length) {
          const i = cursor++;
          const { id, ref } = pairs[i];
          try { await approveDealProposal(ref); }
          catch (err) { console.error('[bulk-approve] failed:', id, ref, err); failed.push(id); }
        }
      })
    );

    if (failed.length > 0) {
      setEntries((prev) => prev.map((e) => {
        if (!failed.includes(e.id)) return e;
        const prevStatus = previousById.get(e.id);
        return prevStatus ? { ...e, status: prevStatus } : e;
      }));
      pushToast({ kind: 'error', text: `${failed.length}件の承認に失敗しました` });
    } else {
      pushToast({ kind: 'success', text: `${pairs.length}件を承認しました` });
    }
  }, [selectedIds, refByEntryId, pushToast]);

  const handleRowClick = useCallback(
    (entry: JournalEntry) => {
      if (activePanelId === entry.id) {
        setActivePanelId(null);
      } else {
        setActivePanelId(entry.id);
        setSidePanelTab(0);
      }
    },
    [activePanelId]
  );

  const handleExpandToggle = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setExpandedId((prev) => (prev === id ? null : id));
    },
    []
  );

  const openCorrection = useCallback((id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCorrectionEntryId(id);
  }, []);

  const closeCorrection = useCallback(() => {
    setCorrectionEntryId(null);
  }, []);

  const handleCorrectionSuccess = useCallback(
    (instructionId: string, action: CorrectionAction) => {
      const entryId = correctionEntryId;
      if (entryId && action === 'reject') {
        setEntries((prev) =>
          prev.map((e) => (e.id === entryId ? { ...e, status: 'blocked' as const } : e)),
        );
      }
      const label =
        action === 'correct' ? '修正指示' :
        action === 'reject'  ? '却下指示' :
                               '再処理依頼';
      pushToast({
        kind: 'success',
        text: `${label}を送信しました (ID: ${instructionId})`,
      });
      setCorrectionEntryId(null);
    },
    [correctionEntryId, pushToast],
  );

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderKpiCard = (
    label: string,
    value: string,
    accentColor: string,
    subtext?: string,
  ) => (
    <CardBase paddingSize="small" overflowHidden={false}>
      <div style={S.kpiCard}>
        <div style={S.kpiAccent(accentColor)} />
        <div style={{ paddingLeft: '8px' }}>
          <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '4px', fontWeight: 500 }}>
            {label}
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#252525', lineHeight: 1.1 }}>
            {value}
          </div>
          {subtext && (
            <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '4px' }}>{subtext}</div>
          )}
        </div>
      </div>
    </CardBase>
  );

  const renderDetailTable = (entry: JournalEntry) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={S.detailTh}>借方科目</th>
          <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
          <th style={{ ...S.detailTh, textAlign: 'right' }}>税額</th>
          <th style={S.detailTh}>税区分</th>
          <th style={S.detailTh}>部門</th>
          <th style={S.detailTh}>品目</th>
          <th style={S.detailTh}>摘要</th>
        </tr>
      </thead>
      <tbody>
        {entry.details.map((d: JournalDetail) => (
          <tr key={d.index}>
            <td style={{ ...S.detailTd, fontWeight: 600, color: '#252525' }}>{d.accountName}</td>
            <td style={{ ...S.detailTd, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(d.amount)}
            </td>
            <td style={{ ...S.detailTd, textAlign: 'right', color: '#8a8580', fontVariantNumeric: 'tabular-nums' }}>
              {fmt(d.taxAmount)}
            </td>
            <td style={S.detailTd}>
              <span style={S.badge('#f5f3f2', '#6b6560')}>{d.taxCodeName}</span>
            </td>
            <td style={S.detailTd}>
              <span style={{ color: '#252525', fontWeight: 500 }}>{d.sectionName}</span>
            </td>
            <td style={S.detailTd}>
              {d.itemName ? (
                <span style={{ color: '#252525' }}>{d.itemName}</span>
              ) : (
                <span style={{ color: '#c4c0bc' }}>--</span>
              )}
            </td>
            <td style={{ ...S.detailTd, maxWidth: '180px' }}>
              <span style={{ color: '#6b6560', fontSize: '0.6875rem' }}>{d.description}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderSidePanel = () => {
    if (!activeEntry) return null;
    const proposal = proposalByEntryId[activeEntry.id];
    const audit: any = proposal?.audit_summary || {};
    return (
      <div style={{ minHeight: '1px' }}>
        <div style={S.sidePanel}>
          <div style={S.sidePanelHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text weight="bold" size={0.875}>
                  {activeEntry.partnerName}
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text size={0.75} color="burnt">
                    #{activeEntry.rowid}
                  </Text>
                  <span style={{ margin: '0 6px', color: '#dbd9d7' }}>|</span>
                  <StatusIcon type={statusToIcon(activeEntry.status)}>
                    {statusToLabel(activeEntry.status)}
                  </StatusIcon>
                </div>
              </div>
              <Button
                small
                appearance="tertiary"
                onClick={() => setActivePanelId(null)}
              >
                閉じる
              </Button>
            </div>
            <div style={{ marginTop: '8px' }}>
              <TabBar
                tabs={['証憑', 'メタデータ']}
                currentTabIndex={sidePanelTab}
                onClickTab={(i) => setSidePanelTab(i)}
                small
              />
            </div>
          </div>

          <div style={S.sidePanelBody}>
            {sidePanelTab === 0 && (
              <div>
                <div style={{ borderBottom: '1px solid #dbd9d7' }}>
                  {(() => {
                    const receipts = receiptsForProposal(proposal);
                    if (receipts.length === 0) {
                      if (!activeEntry.rowid || activeEntry.rowid <= 0) {
                        return (
                          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#8a8580', fontSize: '0.875rem' }}>
                            証憑なし
                          </div>
                        );
                      }
                      return (
                        <PdfViewer
                          url={`https://invoice-review.system-0f7.workers.dev/pdf/${activeEntry.rowid}`}
                          title={activeEntry.partnerName}
                          height="560px"
                        />
                      );
                    }
                    return (
                      <PdfViewer
                        files={receipts}
                        title={activeEntry.partnerName}
                        height="560px"
                      />
                    );
                  })()}
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid #edebea' }}>
                  <HStack gap={0.5}>
                    <Button
                      appearance="primary"
                      small
                      onClick={() => updateStatus(activeEntry.id, 'approved')}
                      disabled={activeEntry.status === 'approved'}
                    >
                      承認する
                    </Button>
                    <Button
                      appearance="secondary"
                      small
                      danger
                      onClick={() => updateStatus(activeEntry.id, 'blocked')}
                      disabled={activeEntry.status === 'blocked'}
                    >
                      却下する
                    </Button>
                    <Button
                      appearance="tertiary"
                      small
                      href={`https://invoice-review.system-0f7.workers.dev/pdf/${activeEntry.rowid}`}
                      target="_blank"
                    >
                      別タブで開く
                    </Button>
                  </HStack>
                  {activeEntry.alreadyRegistered && activeEntry.registeredDealId && (
                    <div style={{ marginTop: '8px', fontSize: '0.75rem' }}>
                      <a
                        href={`https://secure.freee.co.jp/deals#deal_id=${activeEntry.registeredDealId}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          background: '#e6f4ea',
                          color: '#1e6a36',
                          borderRadius: '10px',
                          textDecoration: 'none',
                          fontWeight: 600,
                        }}
                      >
                        freeeで仕訳を開く (deal #{activeEntry.registeredDealId})
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {sidePanelTab === 1 && (
              <div style={{ padding: '16px 20px' }}>
                <VStack gap={0.25}>
                  <SubSectionTitle headlineLevel={4}>請求書情報</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>取引先</span>
                    <span style={S.metaValue}>{activeEntry.partnerName}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>発生日</span>
                    <span style={S.metaValue}>{activeEntry.issueDate || '--'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>引落日</span>
                    <span style={{ ...S.metaValue, color: '#3949ab', fontWeight: 700 }}>
                      {activeEntry.dueDate || '--'}
                    </span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>合計金額</span>
                    <span style={{ ...S.metaValue, fontWeight: 700, fontSize: '0.875rem' }}>{fmt(activeEntry.grandTotal)}</span>
                  </div>
                </VStack>

                <div style={{ marginTop: '16px' }}>
                  <SubSectionTitle headlineLevel={4}>口座引落 検出情報</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>検出フラグ</span>
                    <span style={S.metaValue}>
                      {audit.bank_auto_debit_detected ? (
                        <span style={S.badge('#e8eaf6', '#3949ab')}>口座引落 検出済</span>
                      ) : (
                        <span style={{ color: '#c4c0bc' }}>未検出</span>
                      )}
                    </span>
                  </div>
                  {audit.bank_auto_debit_account && (
                    <div style={S.metaRow}>
                      <span style={S.metaLabel}>引落口座</span>
                      <span style={S.metaValue}>{audit.bank_auto_debit_account}</span>
                    </div>
                  )}
                  {audit.payment_pattern && (
                    <div style={S.metaRow}>
                      <span style={S.metaLabel}>支払パターン</span>
                      <span style={S.metaValue}>{audit.payment_pattern}</span>
                    </div>
                  )}
                  {audit.bank_auto_debit_date && (
                    <div style={S.metaRow}>
                      <span style={S.metaLabel}>引落日 (audit)</span>
                      <span style={S.metaValue}>{audit.bank_auto_debit_date}</span>
                    </div>
                  )}
                </div>

                {activeEntry.notes && (
                  <div style={{ marginTop: '16px' }}>
                    <SubSectionTitle headlineLevel={4}>備考</SubSectionTitle>
                    <div
                      style={{
                        marginTop: '4px',
                        padding: '10px 12px',
                        background: '#f7f5f5',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#6b6560',
                        lineHeight: 1.5,
                      }}
                    >
                      {activeEntry.notes}
                    </div>
                  </div>
                )}

                {activeEntry.flags.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <SubSectionTitle headlineLevel={4}>フラグ</SubSectionTitle>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                      {activeEntry.flags.map((f, i) => (
                        <span key={i} style={S.badge('#e8eaf6', '#3949ab')}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  return (
    <div style={S.page}>
      <Container width="wide">
        <div style={{ padding: '16px 0 8px' }}>
          <Breadcrumbs
            links={[
              { title: 'FREEEMAN', url: '/' },
              { title: '口座引落請求書', url: '#bank_auto_debit_invoice' },
            ]}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <HStack gap={1} alignItems="center">
              <PageTitle>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <MdAccountBalanceWallet />
                  口座引落請求書
                </span>
              </PageTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={S.badge('#e8eaf6', '#3949ab')}>
                  bank_auto_debit
                </span>
                <span style={S.badge('#f5f3f2', '#6b6560')}>
                  引落日が期日
                </span>
              </div>
            </HStack>
            <HStack gap={0.5}>
              <SelectBox
                options={[
                  { name: '引落日 昇順', value: 'dueDate_asc' },
                  { name: '引落日 降順', value: 'dueDate_desc' },
                  { name: '発生日 昇順', value: 'issueDate_asc' },
                  { name: '発生日 降順', value: 'issueDate_desc' },
                  { name: '金額 降順', value: 'amount_desc' },
                  { name: '金額 昇順', value: 'amount_asc' },
                  { name: '取引先', value: 'partnerName_asc' },
                ]}
                value={`${sortKey}_${sortDir}`}
                onChange={(e) => {
                  const [k, d] = e.target.value.split('_') as ['dueDate' | 'issueDate' | 'amount' | 'partnerName', 'asc' | 'desc'];
                  setSortKey(k);
                  setSortDir(d);
                }}
                width="small"
                small
                label="並び順"
              />
            </HStack>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Note>
              口座引落の請求書 ({stats.total}件) — 25日払い とは別フロー。 期日 = 引落日、 自動登録ルール経由で消込。
              対応待ち(承認)=内容を確認して承認/却下 ／ 対応待ち(要修正)=不備を修正すると前進します。
            </Note>
          </div>
        </div>

        <div style={S.kpiGrid}>
          {renderKpiCard('対象件数', `${stats.total}件`, '#3949ab', `${filtered.length}件表示中`)}
          {renderKpiCard('合計金額', fmt(stats.totalAmount), '#252525')}
          {renderKpiCard('対応待ち(承認)', `${stats.pending}件`, '#a46a12', stats.blocked > 0 ? `除外(却下) ${stats.blocked}件` : undefined)}
          {renderKpiCard('引落日 group', `${stats.dueGroupCount}日`,
            '#338650',
            stats.dueGroupList.length > 0
              ? `直近: ${stats.dueGroupList[0][0]} (${stats.dueGroupList[0][1]}件)`
              : undefined,
          )}
        </div>

        <CardBase paddingSize="zero" mb={0.5}>
          <div style={S.filterBar}>
            <SearchField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="取引先名で検索"
              width="large"
              small
            />
            <SelectBox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              width="small"
              small
              label="ステータス"
            />
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Note>{filtered.length}件</Note>
            </div>
          </div>
        </CardBase>

        {loadError && (
          <MessageBlock error mb={0.5}>
            {loadError}
          </MessageBlock>
        )}

        {selectedIds.size > 0 && (
          <MessageBlock success mb={0.5}>
            <HStack gap={1} alignItems="center">
              <Text weight="bold" size={0.875}>
                {selectedIds.size}件選択中
              </Text>
              <Button appearance="primary" small onClick={approveSelected}>
                一括承認
              </Button>
              <Button appearance="tertiary" small onClick={() => setSelectedIds(new Set())}>
                選択解除
              </Button>
            </HStack>
          </MessageBlock>
        )}

        <div style={activePanelId ? S.mainLayoutWithPanel : S.mainLayout}>
          <CardBase paddingSize="zero" overflowHidden={false}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.thCell, width: '36px', textAlign: 'center' }}>
                      <CheckBox
                        checked={selectedIds.size === filtered.length && filtered.length > 0}
                        onChange={toggleSelectAll}
                        small
                      />
                    </th>
                    <th style={{ ...S.thCell, width: '32px', textAlign: 'center' }}>#</th>
                    <th style={S.thCell}>取引先</th>
                    <th style={{ ...S.thCell, textAlign: 'right' }}>金額</th>
                    <th style={S.thCell}>引落日</th>
                    <th style={S.thCell}>引落口座</th>
                    <th style={S.thCell}>部門</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '80px' }}>ステータス</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '80px' }}>アクション</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry, idx) => {
                    const proposal = proposalByEntryId[entry.id];
                    const audit: any = proposal?.audit_summary || {};
                    const account = audit.bank_auto_debit_account || '--';
                    return (
                      <React.Fragment key={entry.id}>
                        <tr
                          style={S.row(
                            selectedIds.has(entry.id),
                            activePanelId === entry.id,
                          )}
                          onClick={() => handleRowClick(entry)}
                        >
                          <td
                            style={{ ...S.tdCell, textAlign: 'center', width: '36px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CheckBox
                              checked={selectedIds.has(entry.id)}
                              onChange={() => toggleSelect(entry.id)}
                              small
                            />
                          </td>
                          <td style={{ ...S.tdCell, textAlign: 'center', width: '32px' }}>
                            <span style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>
                              {idx + 1}
                            </span>
                          </td>
                          <td style={S.tdCell}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontWeight: 600, color: '#252525', fontSize: '0.8125rem' }}>
                                {entry.partnerName}
                              </span>
                            </div>
                            {entry.flags.length > 0 && (
                              <div style={{ marginTop: '2px' }}>
                                {entry.flags.map((f, i) => (
                                  <span key={i} style={S.flagBadge}>{f}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              ...S.tdCell,
                              textAlign: 'right',
                              fontWeight: 700,
                              fontSize: '0.8125rem',
                              fontVariantNumeric: 'tabular-nums',
                              color: '#252525',
                            }}
                          >
                            {fmt(entry.grandTotal)}
                          </td>
                          <td style={S.tdCell}>
                            <span style={{ fontSize: '0.75rem', color: '#3949ab', fontWeight: 600 }}>
                              {entry.dueDate || '--'}
                            </span>
                          </td>
                          <td style={S.tdCell}>
                            <span style={{ fontSize: '0.6875rem', color: '#6b6560' }}>
                              {account}
                            </span>
                          </td>
                          <td style={S.tdCell}>
                            <span style={{ fontSize: '0.75rem', color: '#6b6560' }}>
                              {entry.storeName}
                            </span>
                          </td>
                          <td style={{ ...S.tdCell, textAlign: 'center' }}>
                            <VStack gap={0.25} alignItems="center">
                              <StatusIcon type={statusToIcon(entry.status)}>
                                {statusToLabel(entry.status)}
                              </StatusIcon>
                              {entry.alreadyRegistered && (
                                entry.registeredDealId ? (
                                  <a
                                    href={`https://secure.freee.co.jp/deals#deal_id=${entry.registeredDealId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title={`freeeでdeal#${entry.registeredDealId}を開く`}
                                    style={{
                                      ...S.badge('#e8f5e9', '#2e7d32'),
                                      textDecoration: 'none',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    登録済
                                  </a>
                                ) : (
                                  <span style={S.badge('#e8f5e9', '#2e7d32')}>登録済</span>
                                )
                              )}
                            </VStack>
                          </td>
                          <td
                            style={{ ...S.tdCell, textAlign: 'center', width: '80px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              small
                              appearance="secondary"
                              onClick={(e) => openCorrection(entry.id, e)}
                            >
                              修正
                            </Button>
                          </td>
                          <td
                            style={{ ...S.tdCell, textAlign: 'center', width: '40px' }}
                            onClick={(e) => handleExpandToggle(entry.id, e)}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                fontSize: '0.75rem',
                                color: '#8a8580',
                                transform: expandedId === entry.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              &#9660;
                            </span>
                          </td>
                        </tr>

                        {expandedId === entry.id && (
                          <tr>
                            <td colSpan={10} style={{ padding: 0 }}>
                              <div style={S.expandSection}>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <SectionTitle headlineLevel={4}>
                                    仕訳明細 ({entry.details.length}行)
                                  </SectionTitle>
                                  <HStack gap={0.5}>
                                    <Button
                                      appearance="primary"
                                      small
                                      onClick={() => updateStatus(entry.id, 'approved')}
                                      disabled={entry.status === 'approved'}
                                    >
                                      承認
                                    </Button>
                                    <Button
                                      appearance="secondary"
                                      small
                                      onClick={() => openCorrection(entry.id)}
                                    >
                                      修正
                                    </Button>
                                    <Button
                                      appearance="secondary"
                                      small
                                      danger
                                      onClick={() => updateStatus(entry.id, 'blocked')}
                                      disabled={entry.status === 'blocked'}
                                    >
                                      却下
                                    </Button>
                                  </HStack>
                                </div>

                                <div
                                  style={{
                                    display: 'flex',
                                    gap: '16px',
                                    padding: '8px 12px',
                                    marginBottom: '6px',
                                    background: '#fafafa',
                                    border: '1px solid #e5e4e2',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    color: '#6b6560',
                                  }}
                                >
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>発生日:</b>{' '}
                                    <span style={{ color: '#252525', fontWeight: 600 }}>
                                      {entry.issueDate || '--'}
                                    </span>
                                  </span>
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>引落日:</b>{' '}
                                    <span style={{ color: '#3949ab', fontWeight: 700 }}>
                                      {entry.dueDate || '--'}
                                    </span>
                                  </span>
                                  {account !== '--' && (
                                    <span>
                                      <b style={{ color: '#8a8580', fontWeight: 500 }}>引落口座:</b>{' '}
                                      <span style={{ color: '#252525' }}>{account}</span>
                                    </span>
                                  )}
                                </div>

                                <ColumnBase paddingSize="zero" border="default" rounded>
                                  {renderDetailTable(entry)}
                                </ColumnBase>

                                {entry.notes && (
                                  <div
                                    style={{
                                      marginTop: '10px',
                                      padding: '8px 12px',
                                      background: '#fff',
                                      border: '1px solid #edebea',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      color: '#6b6560',
                                    }}
                                  >
                                    <span style={{ fontWeight: 600, color: '#8a8580', marginRight: '8px' }}>
                                      備考:
                                    </span>
                                    {entry.notes}
                                  </div>
                                )}

                                {!activePanelId && (
                                  <div style={{ marginTop: '12px' }}>
                                    <PdfViewer
                                      url={`https://invoice-review.system-0f7.workers.dev/pdf/${entry.rowid}`}
                                      title={entry.partnerName}
                                      height="450px"
                                    />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ padding: '48px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          <Text size={0.875} color="burnt">
                            {loading
                              ? '読込中...'
                              : '口座引落の請求書はまだありません'}
                          </Text>
                          {!loading && (
                            <Note>
                              vision JSON の grep 検出後、 Claude が deal_proposals に登録すると ここに表示されます。
                            </Note>
                          )}
                        </VStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBase>

          {activePanelId && renderSidePanel()}
        </div>
      </Container>

      <CorrectionModal
        isOpen={correctionEntryId !== null}
        entry={correctionEntryId ? entries.find((e) => e.id === correctionEntryId) ?? null : null}
        deptOptions={[
          { name: 'すべての部門', value: 'all' },
          { name: '本部', value: '本部' },
          { name: 'Bistrot Gourmand', value: 'Bistrot Gourmand' },
          { name: 'La SOFFITTA', value: 'La SOFFITTA' },
          { name: '人間関係', value: '人間関係' },
          { name: '信州屋 渋谷店', value: '信州屋 渋谷店' },
          { name: '信州屋 新宿南口店', value: '信州屋 新宿南口店' },
          { name: 'BiOcafe', value: 'BiOcafe' },
          { name: '山城屋庄藏', value: '山城屋庄藏' },
        ]}
        onClose={closeCorrection}
        onSuccess={handleCorrectionSuccess}
      />

      {toasts.map((t) => (
        <FloatingMessageBlock
          key={t.id}
          success={t.kind === 'success'}
          error={t.kind === 'error'}
          notice={t.kind === 'notice'}
          info={t.kind === 'info'}
          onClose={() =>
            setToasts((prev) => prev.filter((x) => x.id !== t.id))
          }
        >
          {t.text}
        </FloatingMessageBlock>
      ))}
    </div>
  );
};
